import { Controller } from "@hotwired/stimulus"

// This controller wraps stimulus-autocomplete to add provider search-specific behavior
// to the free-text search input. It manages a hidden `selected_npi` field that the
// server uses to shortcut directly to provider lookup (bypassing the LLM).
//
// Behavior:
//   - When a provider is selected from the dropdown: set hidden NPI field, show
//     provider name in the text input, and submit the form.
//   - When the user types freely and presses Enter (no selection): clear the hidden
//     NPI field so the normal LLM flow proceeds.
//   - Clear the NPI field whenever the user modifies the input text (so stale
//     selections don't persist).
//   - Hide the dropdown when there are zero results (the stimulus-autocomplete
//     library has a bug where it shows an empty dropdown).

export default class extends Controller {
  static targets = ["input", "npiField", "results"]

  connect() {
    // Clear NPI on any text input change (user is typing, not selecting)
    this.boundOnInput = this.onInput.bind(this)
    this.inputTarget.addEventListener("input", this.boundOnInput)

    // Watch the results list for changes and hide it when empty.
    // stimulus-autocomplete incorrectly shows the dropdown even with 0 results
    // because !![] is true in JavaScript.
    if (this.hasResultsTarget) {
      this.resultsObserver = new MutationObserver(() => this.hideIfEmpty())
      this.resultsObserver.observe(this.resultsTarget, { childList: true })
    }

    // Dismiss autocomplete when the form is submitted. Uses a CSS class
    // (`autocomplete-suppressed`) instead of setting `hidden` directly because
    // stimulus-autocomplete manages `hidden` via open()/close() and an in-flight
    // fetch response can reopen the dropdown after we close it.
    this.boundDismiss = this.dismiss.bind(this)
    const form = this.element.closest("form")
    if (form) {
      this.form = form
      form.addEventListener("submit", this.boundDismiss)
    }
  }

  disconnect() {
    if (this.boundOnInput) {
      this.inputTarget.removeEventListener("input", this.boundOnInput)
    }
    if (this.resultsObserver) {
      this.resultsObserver.disconnect()
    }
    if (this.form && this.boundDismiss) {
      this.form.removeEventListener("submit", this.boundDismiss)
    }
  }

  // Called on every keystroke in the input field.
  // Clears the hidden NPI field so that if the user edits after selecting,
  // the stale NPI doesn't get submitted.
  onInput() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove("autocomplete-suppressed")
    }
    if (this.hasNpiFieldTarget) {
      this.npiFieldTarget.value = ""
    }
  }

  dismiss() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("autocomplete-suppressed")
    }
  }

  // Called when the user selects an option from the autocomplete dropdown.
  // Wired via: data-action="autocomplete.change->provider-search-autocomplete#onSelect"
  onSelect(event) {
    const npi = event.detail.value
    const selected = event.detail.selected

    // Extract the provider name from the option-heading div (just the name, not the address)
    let providerName = event.detail.textValue
    if (selected && selected.innerHTML.indexOf("option-heading") >= 0) {
      const temp = document.createElement("div")
      temp.innerHTML = selected.innerHTML
      const heading = temp.querySelector(".option-heading")
      if (heading) {
        providerName = heading.textContent.trim()
      }
    }

    // Set the provider name in the visible input
    this.inputTarget.value = providerName

    // Set the NPI in the hidden field
    if (this.hasNpiFieldTarget) {
      this.npiFieldTarget.value = npi
    }

    // Submit the form
    const form = this.inputTarget.closest("form")
    if (form) {
      form.requestSubmit()
    }
  }

  // Hide the results dropdown when it contains no <li> options.
  // This fixes the stimulus-autocomplete bug where an empty dropdown is shown.
  hideIfEmpty() {
    if (!this.hasResultsTarget) return

    const options = this.resultsTarget.querySelectorAll("[role='option']:not([aria-disabled])")
    if (options.length === 0) {
      this.resultsTarget.hidden = true
    }
  }
}
